import { useEffect } from 'react';
import { Box, Drawer, IconButton, Switch, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { matchIsValidTel } from 'mui-tel-input';
import { RxCross2 } from 'react-icons/rx';
import Button from 'components/Common/Button';
import TelInput from 'components/Common/TelInput';
import { useCreateContact } from 'apis/business/agent/contact/hook';

type FormValues = {
  phone_number: string;
  is_vip: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

const DEFAULT_VALUES: FormValues = {
  phone_number: '',
  is_vip: false
};

const BORDER_COLOR = '#E5E7EB';

export default function AddContactDrawer({ open, onClose }: Props) {
  const { mutateAsync: createContact, isPending } = useCreateContact();
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: DEFAULT_VALUES
  });

  useEffect(() => {
    if (open) reset(DEFAULT_VALUES);
  }, [open, reset]);

  const onSubmit = async (data: FormValues) => {
    await createContact({
      contact: {
        phone_number: data.phone_number,
        category: data.is_vip ? 'vip' : 'general'
      }
    });
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 420 },
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2.5}
        borderBottom={`1px solid ${BORDER_COLOR}`}
      >
        <Typography fontSize={18} fontWeight={500}>
          Add Contact
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <RxCross2 size={20} />
        </IconButton>
      </Box>

      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        display='flex'
        flexDirection='column'
        flex={1}
        overflow='hidden'
      >
        <Box flex={1} overflow='auto' px={3} py={3} display='flex' flexDirection='column' gap={3}>
          <TelInput
            name='phone_number'
            control={control}
            label='Number'
            defaultCountry='US'
            rules={{
              required: 'Number is required',
              validate: (value: string) => matchIsValidTel(value) || 'Enter a valid phone number'
            }}
          />

          <Box display='flex' alignItems='center' justifyContent='space-between' gap={2}>
            <Box>
              <Typography fontSize={14} fontWeight={500}>
                This is a VIP Customer
              </Typography>
              <Typography fontSize={13} color='text.secondary'>
                Enable for VIP customers
              </Typography>
            </Box>
            <Controller
              name='is_vip'
              control={control}
              render={({ field }) => (
                <Switch
                  checked={!!field.value}
                  onChange={(_, checked) => field.onChange(checked)}
                  inputProps={{ 'aria-label': 'VIP customer' }}
                />
              )}
            />
          </Box>
        </Box>

        <Box px={3} py={2} display='flex' justifyContent='flex-end' gap={1.5} borderTop={`1px solid ${BORDER_COLOR}`}>
          <Button
            variant='text'
            onClick={onClose}
            disabled={isPending}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              border: '1px solid #c9c8c8',
              px: 1.5
            }}
          >
            Cancel
          </Button>
          <Button type='submit' variant='contained' isPending={isPending} loadingText='Adding'>
            Add
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
